import * as React from 'react';
import { cn } from '@/shared/infra/ui/lib/utils';
import { FieldMessage } from '@/shared/infra/ui/components/ui/field-message';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  invalid?: boolean;
  error?: Array<{ message?: string } | undefined>;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      className,
      type,
      id,
      invalid,
      error,
      placeholder,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const inputPlaceholder = placeholder ?? (label ? ' ' : undefined);
    const hasMessage = !!description || !!error?.some((e) => e?.message);
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = hasMessage ? `${inputId}-error` : undefined;
    // The help text is only painted on focus, but a screen reader hears it whenever the field is read.
    const ariaDescribedBy =
      [describedBy, descriptionId, invalid ? errorId : undefined].filter(Boolean).join(' ') ||
      undefined;

    return (
      <div className="group/input w-full">
        {/* The label anchors to THIS box, which must contain nothing but the input: its resting
            position is `top-1/2`, so anything else in the positioning context (like the message
            slot below) drags the label off the input's own center. */}
        <div className="relative w-full">
          <input
            id={inputId}
            type={type}
            placeholder={inputPlaceholder}
            className={cn(
              'peer flex h-12 w-full rounded border bg-transparent px-4 text-base transition-all',
              'placeholder:text-center placeholder:text-muted-foreground placeholder-transparent',
              'caret-primary',
              'focus:border-primary',
              'focus:outline-none focus:border-1',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
              className
            )}
            ref={ref}
            aria-describedby={ariaDescribedBy}
            {...props}
          />
          {label ? (
            <label
              htmlFor={inputId}
              className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 px-1 text-sm text-muted-foreground transition-all pointer-events-none bg-transparent',
                'peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:bg-white',
                'peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium peer-focus:bg-white peer-focus:text-primary'
              )}
            >
              {label}
            </label>
          ) : null}
        </div>
        {/* One slot for both messages, so neither focus nor a failed validation can change the
            field's height. Outside the label's positioning box — see above. */}
        {description || error ? (
          <FieldMessage
            description={description}
            errors={error}
            invalid={invalid}
            descriptionId={descriptionId}
            errorId={errorId}
          />
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
export { Input };
